import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { finalize, of, Subscription, tap } from 'rxjs';
import { catchError, debounceTime, filter, switchMap } from 'rxjs/operators';
import { CountryCode } from 'libphonenumber-js';

import {
  ValidationService,
  TelService,
} from '@tablesready/common-ui';
import { CustomersService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { FieldConfig } from '@shared/restaurant/models';

interface ICustomer {
  address1: string;
  birthday: {month: number, day: number, year: number};
  city: string;
  created_time: number;
  email: string;
  lastVisit: string;
  name: string;
  note: string;
  phone: string;
  restaurant: string;
  state: string;
  updated_time: number;
  visits: string[];
  zip: string;
  _id: string;
  customFields: Record<string, unknown>;
}

interface ISettings {
  country: string;
  customer: string;
  customFields: FieldConfig[];
}

@Component({
  selector: 'tablesready-add-edit-customer',
  templateUrl: './add-edit-customer.component.html',
  styleUrls: ['./add-edit-customer.component.scss'],
})
export class AddEditCustomerComponent implements OnInit, OnDestroy {
  icon = AppIcon;
  @Input() title: string;
  @Input() customer: ICustomer;
  @Input() settings: ISettings;
  @Input() editMode = false;
  @Input() isOnFreePlan = false;

  daysRange: number[];
  monthsRange: number[];
  yearsRange: number[];
  stateLabelText: string;
  zipLabelText: string;

  SHORTEST_PHONE_DIGITS = 4;

  customerForm: UntypedFormGroup;
  private subscription = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly fb: UntypedFormBuilder,
    private readonly customersService: CustomersService,
    private readonly alertsService: AlertsService,
    private readonly validationService: ValidationService,
    private readonly telService: TelService,
  ) { }

  ngOnInit(): void {
    this.daysRange = this.createRangeArray(1, 31);
    this.monthsRange = this.createRangeArray(1, 12);
    this.yearsRange = this.createRangeArray(1930, new Date().getFullYear());

    this.customerForm = this.fb.group({
      name: [this.customer.name, [Validators.required, Validators.maxLength(2000)]],
      email: [this.customer.email, [Validators.email, Validators.maxLength(100)]],
      phone: [this.telService.format(this.customer.phone), [Validators.required, this.validationService.phonePatternCountryValidator(this.settings.country)]],
      birthdayMonth: [this.customer.birthday?.month, [Validators.max(12)]],
      birthdayDay: [this.customer.birthday?.day, [Validators.max(31)]],
      birthdayYear: [this.customer.birthday?.year],
      address: [this.customer.address1, [Validators.maxLength(255)]],
      city: [this.customer.city, [Validators.maxLength(50)]],
      state: [this.customer.state, [Validators.maxLength(50)]],
      zip: [this.customer.zip, [Validators.maxLength(10)]],
      note: [this.customer.note, [Validators.maxLength(1024)]],
      customFields: [this.customer.customFields || {}],
    });

    this.stateLabelText = this.setState();
    this.zipLabelText = this.setZip();

    this.subscription.add(
      this.customerForm.get('phone').valueChanges.pipe(
        filter((phone) => {
          return !(phone?.length < this.SHORTEST_PHONE_DIGITS || this.customerForm.value.name?.length);
        }),
        debounceTime(400),
        switchMap((phone) => {
          return this.customersService.customerPhoneLookup(phone);
        })
      ).subscribe(data => {
        this.customerForm.patchValue({
          name: data
        });
      })
    );
  }

  save (): void {
    const customer = this.customerForm.value;
    const country: CountryCode = this.settings.country === 'UK' ? 'GB' : (this.settings.country as CountryCode);
    this.customer.phone = this.telService.format(customer.phone, country, 'intl');
    this.customer.name = customer.name;
    this.customer.email = customer.email;
    this.customer.birthday = {
      'month': customer.birthdayMonth,
      'day': customer.birthdayDay,
      'year': customer.birthdayYear
    };
    this.customer.address1 = customer.address;
    this.customer.city = customer.city;
    this.customer.state = customer.state;
    this.customer.zip = customer.zip;
    this.customer.note = customer.note;
    this.customer.customFields = customer.customFields;

    const message = this.title.toLowerCase().split(' ');
    const _customer = {
      _id: this.customer._id,
      phone: this.customer.phone,
      name: this.customer.name,
      email: this.customer.email,
      birthday: this.customer.birthday,
      address1: this.customer.address1,
      city: this.customer.city,
      state: this.customer.state,
      zip: this.customer.zip,
      note: this.customer.note,
      customFields: this.customer.customFields,
    };

    this.customersService.saveCustomer(_customer)
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: `${message[1]} ${message[0]}ed.`,
          });
        }),
        catchError((error) => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `${this.editMode ? '' : 'new '}customer not ${this.editMode ? 'updated' : 'added'} successfully. ${error}.`,
          });

          return of(error);
        }),
        finalize(() => {
          void this.modalController.dismiss();
        }),
      )
      .subscribe();
  }

  generateDaysOfMonth() {
    const value = this.customerForm.value;
    let maxDays;
    switch (value.birthdayMonth) {
      case  '4' || '6' || '9' || '11':
        maxDays = 30
        break;
      case '2':
        maxDays = value.birthdayYear%4 ? 28 : 29;
        break;
      default:
        maxDays = 31
    }
    this.customerForm.patchValue({
      birthdayDay: null
    });
    this.daysRange = this.createRangeArray(1, maxDays);
  }

  validateField(value: string): boolean {
    return this.customerForm.get(value).invalid && this.customerForm.get(value).touched;
  }

  private setState() {
    switch (this.settings.country) {
      case 'US':
        return 'State';
      case 'CA':
        return 'Province';
      case 'GB':
        return 'County';
      case 'UK':
        return 'County';
      default:
        return 'State/region';
    }
  }

  private setZip() {
    switch (this.settings.country) {
      case 'US':
        return 'Zip';
      case 'CA':
        return 'Postal Code';
      case 'GB':
        return 'Postcode';
      case 'UK':
        return 'Postcode';
      default:
        return 'Postal code';
    }
  }

  private createRangeArray(start, end) {
    const arr = [];
    for (let i = start; i <= end; i++) {
      arr.push(i);
    }
    return arr;
  }

  cancel(): void {
    void this.modalController.dismiss();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
